<template>
  <div
    class="content-teaser"
    :class="{
      '-home': data.teaser.some((t) => t.__typename === 'TeaserHomeRecord'),
      '-big': data.teaser.some((t) => t.__typename === 'TeaserBigRecord'),
      '-small': data.teaser.some((t) => t.__typename === 'TeaserSmallRecord'),
    }"
  >
    <div class="grid-container">
      <div class="inner">
        <div class="titlewrapper">
          <h2 v-if="data.title" class="title" v-html="data.title" />
          <StructuredText v-if="data.lead" class="structured-text lead" :data="data.lead" />
        </div>
        <div class="itemwrapper">
          <div v-for="t in data.teaser" :key="t.id" class="item">
            <TeaserHome v-if="t.__typename === 'TeaserHomeRecord'" :data="t" />
            <TeaserBig v-if="t.__typename === 'TeaserBigRecord'" :data="t" />
            <TeaserSmall v-if="t.__typename === 'TeaserSmallRecord'" :data="t" />
          </div>
          <div v-if="data.teaser.some((t) => t.__typename === 'TeaserSmallRecord')" class="pattern" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { StructuredText } from 'vue-datocms';
import type { ContentTeaserFragment } from '#gql';
import TeaserBig from '~/components/content-modules/components/TeaserBig.vue';
import TeaserSmall from '~/components/content-modules/components/TeaserSmall.vue';
import TeaserHome from '~/components/content-modules/components/TeaserHome.vue';

defineProps<{
  data: ContentTeaserFragment;
}>();
</script>

<style scoped lang="scss">
.content-teaser {
  /* TEASER HOME */
  &.-home {
    .inner {
      > .itemwrapper {
        display: flex;
        justify-content: center;
        gap: var(--spacing-xs);

        @media (--xs), (--sm) {
          padding: var(--spacing-sm) 0;
          flex-direction: column;
        }

        @media (--md), (--lg) {
          padding: var(--spacing-xs) 0;
        }

        > .item {
          width: 100%;

          &:nth-of-type(1) > .teaser-home {
            background: var(--color-sky-light);
            color: var(--color-sky-dark);

            :deep(.title),
            :deep(.button) {
              color: var(--color-sky-dark);

              &:focus-visible {
                border: 2px solid var(--color-sky-dark);
              }

              @media (hover: hover) and (pointer: fine) {
                &:hover > .iconwrapper {
                  background: var(--color-sky-dark);
                }

                &:hover > .iconwrapper > .icon {
                  fill: var(--color-white);
                }
              }

              > .iconwrapper {
                background: var(--color-white);

                > .icon {
                  fill: var(--color-sky-dark);
                }
              }
            }
          }

          &:nth-of-type(2) > .teaser-home {
            background: var(--color-turquoise-light);
            color: var(--color-turquoise-dark);

            :deep(.title),
            :deep(.button) {
              color: var(--color-turquoise-dark);

              &:focus-visible {
                border: 2px solid var(--color-turquoise-dark);
              }

              @media (hover: hover) and (pointer: fine) {
                &:hover > .iconwrapper {
                  background: var(--color-turquoise-dark);
                }

                &:hover > .iconwrapper > .icon {
                  fill: var(--color-white);
                }
              }

              > .iconwrapper {
                background: var(--color-white);

                > .icon {
                  fill: var(--color-turquoise-dark);
                }
              }
            }
          }

          &:nth-of-type(3) > .teaser-home {
            background: var(--color-aprico-light);
            color: var(--color-aprico-dark);

            :deep(.title),
            :deep(.button) {
              color: var(--color-aprico-dark);

              &:focus-visible {
                border: 2px solid var(--color-aprico-dark);
              }

              @media (hover: hover) and (pointer: fine) {
                &:hover > .iconwrapper {
                  background: var(--color-aprico-dark);
                }

                &:hover > .iconwrapper > .icon {
                  fill: var(--color-white);
                }
              }

              > .iconwrapper {
                background: var(--color-white);

                > .icon {
                  fill: var(--color-aprico-dark);
                }
              }
            }
          }
        }
      }
    }
  }

  /* TEASER BIG */
  &.-big {
    .inner {
      display: grid;
      gap: var(--spacing-sm);

      @media (--xs), (--sm) {
        padding: var(--spacing-md) 0;
      }

      @media (--md), (--lg) {
        padding: var(--spacing-3xl) 0;
      }

      > .titlewrapper {
        display: grid;
        column-gap: var(--spacing-xs);
        row-gap: var(--spacing-2xs);

        @media (--md), (--lg) {
          grid-template-columns: repeat(22, 1fr);
        }

        > .title,
        > .lead {
          @media (--md), (--lg) {
            grid-column: 3 / 21;
          }
        }
      }

      > .itemwrapper {
        display: grid;

        @media (--md), (--lg) {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          column-gap: var(--spacing-xs);
        }

        > .item:nth-of-type(1) {
          @media (--xs), (--sm) {
            margin-bottom: var(--spacing-2xs);
          }
        }
      }
    }
  }

  /* TEASER SMALL */
  &.-small {
    width: 100%;
    background: var(--color-forest-light);
    position: relative;

    @media (--xs), (--sm) {
      padding: var(--spacing-2xl) 0;
      margin: var(--spacing-2xl) 0;
    }

    @media (--md), (--lg) {
      margin: var(--spacing-3xl) 0;
      padding: var(--spacing-4xl) 0;
    }

    @media (--lg-custom) {
      padding: var(--spacing-4xl) 0;
    }

    .inner {
      color: var(--color-forest-dark);

      > .titlewrapper {
        grid-column: 1 / -1;

        @media (--xs), (--sm) {
          margin-bottom: var(--spacing-xs);
        }

        @media (--md), (--lg) {
          margin-bottom: var(--spacing-sm);
        }

        :deep(.title) {
          color: var(--color-forest-dark);
        }
      }

      > .itemwrapper {
        display: grid;
        position: relative;
        width: 100%;
        z-index: 1;

        @media (--xs) {
          gap: var(--spacing-2xs);
        }

        @media (--sm), (--md), (--lg) {
          column-gap: var(--spacing-2xs);
          row-gap: calc(2 * var(--spacing-2xs));
        }

        @media (--md), (--lg) {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @media (--lg-custom) {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        > .item {
          // add hover effect on teaser small on Tablet Portrait too
          @media (--sm) {
            @media (hover: hover) and (pointer: fine) {
              &:hover {
                transform: translateY(-1.5rem);
              }
            }
          }
        }

        > .pattern {
          position: absolute;
          z-index: -1;
          background: url('/design-elements/pattern.svg') right;
          width: 100%;
          height: 100%;

          @media (--xs), (--sm) {
            bottom: -1.5rem;
            right: -1.5rem;
          }

          @media (--md), (--lg) {
            bottom: -3rem;
            right: -3rem;
          }
        }
      }
    }
  }
}

.grid-container {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
    margin: auto;
  }

  .inner {
    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--sm), (--md), (--lg) {
      grid-column: 2 / 24;
    }

    > .titlewrapper {
      hyphens: none;

      > .title,
      > .lead {
        max-width: 900px;
      }
    }

    > .itemwrapper > .item {
      transition: transform 200ms;

      @media (--md), (--lg) {
        @media (hover: hover) and (pointer: fine) {
          &:hover {
            transform: translateY(-1.5rem);
          }
        }
      }
    }
  }
}
</style>
